import { BUSINESS_TIME_ZONE, Decimal } from '@paguz/contracts';

/**
 * Formatos de presentación de la consola (es-CO, America/Bogota). Solo presentan valores que
 * llegan de la API; ningún cálculo financiero vive aquí.
 */

const dateTimeFormat = new Intl.DateTimeFormat('es-CO', {
  timeZone: BUSINESS_TIME_ZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

const timeFormat = new Intl.DateTimeFormat('es-CO', {
  timeZone: BUSINESS_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const longDateFormat = new Intl.DateTimeFormat('es-CO', {
  timeZone: BUSINESS_TIME_ZONE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** Fecha de negocio (`YYYY-MM-DD`): se formatea en UTC para no correr el día. */
const businessDateFormat = new Intl.DateTimeFormat('es-CO', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const compactInt = new Intl.NumberFormat('es-CO');

/** Arma `5 nov 2026` con partes (los motores difieren: "5 de nov. de 2026"). */
function compactDate(format: Intl.DateTimeFormat, date: Date): string {
  const parts = format.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('day')} ${part('month').replace('.', '')} ${part('year')}`;
}

/** `30 oct 2026, 11:10 a. m.` */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const parts = dateTimeFormat.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const period = normalizeSpaces(part('dayPeriod'));
  return `${compactDate(dateTimeFormat, date)}, ${part('hour')}:${part('minute')}${period ? ` ${period}` : ''}`;
}

/** `30 oct · 11:10` (compacto, 24 h, para líneas de tiempo). */
export function formatShortDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const parts = dateTimeFormat.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const time = timeFormat.formatToParts(date);
  const t = (type: Intl.DateTimeFormatPartTypes) => time.find((p) => p.type === type)?.value ?? '';
  return `${part('day')} ${part('month').replace('.', '')} · ${t('hour')}:${t('minute')}`;
}

/** `11:10:04.218` (hora de Bogotá con milisegundos, para la saga). */
export function formatTimeMs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const ms = String(date.getUTCMilliseconds()).padStart(3, '0');
  return `${timeFormat.format(date)}.${ms}`;
}

/** `jueves, 30 de octubre de 2026` */
export function formatLongDate(iso: string): string {
  return longDateFormat.format(new Date(iso));
}

/** `5 nov 2026` desde una fecha de negocio. */
export function formatBusinessDate(date: string | null | undefined): string {
  if (!date) return '—';
  return compactDate(businessDateFormat, new Date(`${date}T12:00:00Z`));
}

/** `45,21 % EA` desde una tasa decimal (`"0.452100"`). */
export function formatRate(rate: string | null | undefined, suffix = 'EA', decimals = 2): string {
  if (rate === null || rate === undefined || rate === '') return '—';
  const percent = new Decimal(rate).times(100).toFixed(decimals);
  return `${percent.replace('.', ',')} %${suffix ? ` ${suffix}` : ''}`;
}

/** Tasa decimal (`"0.452100"`) → porcentaje editable (`"45,21"`). */
export function rateToPercentInput(rate: string, decimals = 4): string {
  const value = new Decimal(rate).times(100);
  return value.toDecimalPlaces(decimals).toString().replace('.', ',');
}

/**
 * Porcentaje escrito por el operador (`"45,21"`, `"0,4"`) → tasa decimal de 6 decimales.
 * Devuelve `null` si no es un número válido.
 */
export function percentInputToRate(input: string): string | null {
  const normalized = input.trim().replace(/\s|%/g, '').replace(',', '.');
  if (!/^\d{1,3}(\.\d{1,4})?$/.test(normalized)) return null;
  return new Decimal(normalized).dividedBy(100).toFixed(6);
}

/** Monto escrito (string decimal sin separadores, de `AmountInput`) → `"1200000.00"`. */
export function toMoneyAmount(value: string): string {
  return new Decimal(value === '' ? '0' : value).toFixed(2);
}

/** `"2000000.00"` → `"2000000"` para `AmountInput`. */
export function fromMoneyAmount(amount: string): string {
  return new Decimal(amount).toDecimalPlaces(0).toFixed(0);
}

/** Duración legible: `12 ms`, `1,82 s`, `2 min 5 s`. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2).replace('.', ',')} s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} s`;
}

export function formatInt(value: number): string {
  return compactInt.format(value);
}

/** Recorta IDs largos para tablas: `pi_06A0…4Q8M`. */
export function shortId(id: string, head = 8, tail = 4): string {
  if (id.length <= head + tail + 1) return id;
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}

function normalizeSpaces(value: string): string {
  return value.replace(/[\u202f\u00a0]/g, ' ');
}
